import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { apiClient, Mui, useTheme } from "@leap4/frontend";
import { dashboardComputeUrl, dashboardDailyUrl } from "../../../config/generals";
import { useAppSelector } from "../../../store/hooks";
import { isCanceledError } from "../../../utils/functions";
import { createRequestCancellation, IRequestCancellationConfig } from "../../../utils/requestCancellation";
import { seriesColors } from "../../../theme/colors";
import { useCampaignScope } from "../../scope/CampaignScopeProvider";
import { useLocalStorage } from "../../layout/useLocalStorage";
import { findMissingDateRanges } from "./cacheCoverage";
import { MetricCard } from "./MetricCard";
import { DashboardLoadingChart } from "./DashboardLoadingChart";
import { RunTimer } from "../reporting/base/runTimer";
import { formatMetricValue, IChartComparison, IChartMetric, IChartPoint, IMaturingWindow, PerformanceChart } from "./PerformanceChart";
import { presetRange, TimeRangeSelector } from "./TimeRangeSelector";

dayjs.extend(utc);

const { Box, Paper, Typography, ToggleButton, ToggleButtonGroup, Chip, Alert, Button, useMediaQuery } = Mui;

type DateBasis = "impression" | "conversion";
type Interval = "Day" | "Month";
type MetricKey = string;

interface IDashboardMetric {
    key: string;
    event: string | null;
    label: string;
    countingMode: "served" | "raw" | "attributed";
    dateBasis: "served" | "event" | "impression";
    attributionWindowDays: number;
    /** Colour slot, from the backend, so this page never needs a client or event name. */
    tone: "served" | "primary" | "secondary" | "tertiary" | "quaternary" | "quinary";
    /** The paying event: its window drives the maturing shading. */
    primary: boolean;
}

interface IDailyRow {
    date: string;
    impressions: number;
    [key: string]: string | number;
}

interface IDailyWindow {
    campaign: string;
    dateBasis: DateBasis;
    windowStart: string;
    windowEnd: string;
    computedAt: string | null;
    daily: IDailyRow[];
    metrics: IDashboardMetric[];
}

const COMPARISONS = [
    { id: "d1", label: "Previous day", offsetDays: 1, color: "#ec4899" },
    { id: "w1", label: "Previous week", offsetDays: 7, color: "#0ea5e9" },
    { id: "m1", label: "4 weeks ago", offsetDays: 28, color: "#94a3b8" },
];

const SERIES_STORAGE_KEY = "portal-dashboard-hidden-events-v3";
const CHART_HEIGHT = 350;
const YMD = "YYYY-MM-DD";
const EMPTY_METRICS: IDashboardMetric[] = [];

const metricBasisLabel = (metric: IDashboardMetric): string =>
    metric.dateBasis === "served" ? "by served date"
        : metric.dateBasis === "impression" ? "by impression date"
        : metric.countingMode === "raw" ? "by event date · unattributed" : "by event date";

const metricHelp = (metric: IDashboardMetric): string => {
    if (metric.countingMode === "served") return "All impressions served in the selected date range.";
    if (metric.countingMode === "raw") return "Raw advertiser events counted on event date, without impression attribution. The date-basis toggle does not change this metric.";
    return metric.dateBasis === "event"
        ? "Attributed event credit for events that occurred in the period. Fractional attribution is preserved."
        : "Event credit attributed to impressions served in the period. Fractional attribution is preserved.";
};

const TONE_COLORS: Record<IDashboardMetric["tone"], string> = {
    served: seriesColors.impressions,
    primary: seriesColors.installs,
    secondary: seriesColors.visits,
    tertiary: "#0ea5e9",
    quaternary: "#E11D48",
    quinary: "#FACC15",
};

const bucketKey = (date: string, interval: Interval) => (interval === "Month" ? date.slice(0, 7) : date);

const buildBuckets = (start: string, end: string, interval: Interval): { key: string; label: string }[] => {
    const unit = interval === "Month" ? "month" : "day";
    const out: { key: string; label: string }[] = [];
    let cursor = interval === "Month" ? dayjs(start).startOf("month") : dayjs(start);
    const last = interval === "Month" ? dayjs(end).startOf("month") : dayjs(end);
    while (!cursor.isAfter(last) && out.length < 1000) {
        out.push({ key: cursor.format(interval === "Month" ? "YYYY-MM" : YMD), label: cursor.format(interval === "Month" ? "MMM YYYY" : "MMM D") });
        cursor = cursor.add(1, unit);
    }
    return out;
};

/** First bucket still inside the window: its counts can keep growing. */
const maturingFrom = (buckets: { key: string }[], interval: Interval, windowDays: number): string | null => {
    if (!windowDays) return null;
    const cutoff = dayjs().subtract(windowDays, "day").startOf("day");
    const hit = buckets.find((b) => (interval === "Month" ? dayjs(`${b.key}-01`).endOf("month") : dayjs(b.key)).isAfter(cutoff));
    return hit ? hit.key : null;
};

type LoadStatus = "idle" | "loading" | "ready" | "error";

// The handoff's warn tokens: amber reads as "still settling", not as an error.
const maturingChipSx = {
    height: 20,
    bgcolor: (t: any) => (t.palette.mode === "dark" ? "#3A2E12" : "#FDF1D8"),
    color: (t: any) => (t.palette.mode === "dark" ? "#F2C66B" : "#8A5A00"),
};

const DashboardContent = (): JSX.Element => {
    const theme = useTheme();
    const compact = useMediaQuery(theme.breakpoints.down("sm"));
    const selectedOrganizationId = useAppSelector((state: any) => state.auth?.selectedOrganizationId);
    const { campaign, campaigns, status: scopeStatus } = useCampaignScope();

    const initial = presetRange(14);
    const [startDate, setStartDate] = useState(initial.start);
    const [endDate, setEndDate] = useState(initial.end);
    const [dateBasis, setDateBasis] = useState<DateBasis>("impression");
    const [interval, setIntervalValue] = useState<Interval>("Day");
    const [comparisonIds, setComparisonIds] = useState<string[]>([]);
    // null = never customised, so the campaign default applies.
    const [hiddenEvents, setHiddenEvents] = useLocalStorage<string[] | null>(`${SERIES_STORAGE_KEY}:${selectedOrganizationId}:${campaign}`, null);
    const [focusKey, setFocusKey] = useState<string | null>(null);
    const [previewKey, setPreviewKey] = useState<string | null>(null);
    const [metricState, setMetricState] = useState<{ key: string; metrics: IDashboardMetric[] }>({ key: "", metrics: [] });
    const [reloadKey, setReloadKey] = useState(0);

    const scopeKey = JSON.stringify([selectedOrganizationId, campaign, dateBasis]);
    const metrics = metricState.key === scopeKey ? metricState.metrics : EMPTY_METRICS;
    const METRIC_KEYS = useMemo(() => metrics.map((m) => m.key), [metrics]);
    const metricOf = useMemo(() => new Map(metrics.map((m) => [m.key, m])), [metrics]);
    const chartMetrics = useMemo<IChartMetric[]>(() => metrics.map<IChartMetric>((metric) => ({
        key: metric.key, label: metric.label, color: TONE_COLORS[metric.tone] ?? seriesColors.installs,
        type: metric.countingMode === "served" ? "bar" : "line",
        yAxisId: metric.key,
        decimals: metric.countingMode === "attributed" ? 1 : 0,
        description: metricHelp(metric),
        primary: metric.primary,
    })), [metrics]);
    // With more than two events, start on the first and the paying one; the rest are a click away.
    const defaultHidden = useMemo(() => {
        const events = metrics.filter((m) => m.countingMode !== "served");
        if (events.length <= 2) return [];
        const shown = new Set([events[0].key, events.find((m) => m.primary)?.key]);
        return events.filter((m) => !shown.has(m.key)).map((m) => m.event ?? m.key);
    }, [metrics]);
    const hidden = Array.isArray(hiddenEvents) ? hiddenEvents : defaultHidden;
    const visibleKeys = metrics.filter((m) => !hidden.includes(m.event ?? m.key)).map((m) => m.key);
    const setVisibleKeys = (keys: string[]) => setHiddenEvents(metrics.filter((m) => !keys.includes(m.key)).map((m) => m.event ?? m.key));
    const toggleSeries = (key: string) =>
        setVisibleKeys(visibleKeys.includes(key) ? visibleKeys.filter((k) => k !== key) : chartMetrics.map((m) => m.key).filter((k) => k === key || visibleKeys.includes(k)));
    // A hovered card previews its hidden series until the pointer leaves.
    const chartVisibleKeys = previewKey && !visibleKeys.includes(previewKey) ? [...visibleKeys, previewKey] : visibleKeys;

    // Rows already fetched for this scope; the cache window and live gap-fills both land here.
    const rowsRef = useRef<Map<string, IDailyRow>>(new Map());
    const rowsScopeRef = useRef(scopeKey);
    const [rowsVersion, setRowsVersion] = useState(0);
    const [windowStatus, setWindowStatus] = useState<{ key: string; settled: boolean }>({ key: "", settled: false });
    const [rangeStatus, setRangeStatus] = useState<LoadStatus>("idle");

    const mergeRows = useCallback((rows: IDailyRow[]) => {
        for (const row of rows) rowsRef.current.set(row.date, row);
        setRowsVersion((v) => v + 1);
    }, []);

    if (rowsScopeRef.current !== scopeKey) {
        rowsScopeRef.current = scopeKey;
        rowsRef.current = new Map();
    }

    // The precomputed window serves most ranges instantly; a 404 just means this campaign has no cache yet.
    useEffect(() => {
        if (!campaign) return undefined;
        const cancellation = createRequestCancellation();
        let cancelled = false;
        setWindowStatus({ key: scopeKey, settled: false });
        apiClient
            .get<IDailyWindow>(`${dashboardDailyUrl}?campaign=${encodeURIComponent(campaign)}&dateBasis=${dateBasis}`, cancellation.config)
            .then((res) => {
                if (res?.data?.dateBasis !== dateBasis) throw new Error("Dashboard response date basis does not match the selection");
                if (!Array.isArray(res.data.metrics)) throw new Error("Dashboard metric definitions are missing");
                if (!cancelled && rowsScopeRef.current === scopeKey) {
                    setMetricState({ key: scopeKey, metrics: res.data.metrics });
                    mergeRows(res.data.daily ?? []);
                }
            })
            .catch((err) => {
                if (isCanceledError(err)) return;
            })
            .finally(() => {
                if (!cancelled) setWindowStatus({ key: scopeKey, settled: true });
            });
        return () => {
            cancelled = true;
            cancellation.cancel();
        };
    }, [campaign, dateBasis, scopeKey, mergeRows, reloadKey]);

    const fetchComputed = useCallback(
        async (start: string, end: string, config?: IRequestCancellationConfig): Promise<IDailyRow[]> => {
            const params = new URLSearchParams({ campaign, startDate: start, endDate: end, dateBasis });
            const res = await apiClient.get<{ dateBasis: DateBasis; daily: IDailyRow[]; metrics: IDashboardMetric[] }>(`${dashboardComputeUrl}?${params.toString()}`, config);
            if (res?.data?.dateBasis !== dateBasis) throw new Error("Dashboard response date basis does not match the selection");
            if (!Array.isArray(res.data.metrics)) throw new Error("Dashboard metric definitions are missing");
            if (rowsScopeRef.current === scopeKey) setMetricState({ key: scopeKey, metrics: res.data.metrics });
            return res.data.daily ?? [];
        },
        [campaign, dateBasis, scopeKey],
    );

    const maxOffset = interval === "Month" ? 0 : COMPARISONS.filter((c) => comparisonIds.includes(c.id)).reduce((m, c) => Math.max(m, c.offsetDays), 0);
    const needStart = dayjs(startDate).subtract(maxOffset, "day").format(YMD);
    const windowSettled = windowStatus.key === scopeKey && windowStatus.settled;

    // Live-compute only the dates the cache window did not cover.
    useEffect(() => {
        if (!campaign || !windowSettled) {
            setRangeStatus("loading");
            return undefined;
        }
        const gaps = findMissingDateRanges(needStart, endDate, new Set(rowsRef.current.keys()));
        if (!gaps.length) {
            setRangeStatus("ready");
            return undefined;
        }
        const cancellation = createRequestCancellation();
        const scopeAtStart = scopeKey;
        setRangeStatus("loading");
        (async () => {
            try {
                for (const [gs, ge] of gaps) {
                    if (gs > ge) continue;
                    const rows = await fetchComputed(gs, ge, cancellation.config);
                    if (rowsScopeRef.current !== scopeAtStart) return;
                    mergeRows(rows);
                }
                setRangeStatus("ready");
            } catch (err) {
                if (isCanceledError(err)) return;
                console.error("Failed to fetch dashboard data:", err);
                setRangeStatus("error");
            }
        })();
        return () => cancellation.cancel();
        // rowsVersion is deliberately absent: merging rows must not re-trigger the fetch that produced them.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaign, scopeKey, windowSettled, needStart, endDate, fetchComputed, mergeRows, reloadKey]);

    const rowsInRange = useMemo(() => {
        const out: IDailyRow[] = [];
        rowsRef.current.forEach((row) => {
            if (row.date >= startDate && row.date <= endDate) out.push(row);
        });
        return out;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowsVersion, startDate, endDate, scopeKey]);

    const rangeComplete = useMemo(
        () => findMissingDateRanges(startDate, endDate, new Set(rowsInRange.map((r) => r.date))).length === 0,
        [rowsInRange, startDate, endDate],
    );

    const totals = useMemo(() => {
        if (!rangeComplete) return null;
        return rowsInRange.reduce((acc, row) => {
            METRIC_KEYS.forEach((key) => { acc[key] += Number(row[key]) || 0; });
            return acc;
        }, Object.fromEntries(METRIC_KEYS.map((key) => [key, 0])) as Record<string, number>);
    }, [rowsInRange, rangeComplete, METRIC_KEYS]);

    const shownKeys = METRIC_KEYS;
    const campaignMetrics = chartMetrics.filter((m) => shownKeys.includes(m.key as MetricKey));
    const visibleMetrics = campaignMetrics.filter((m) => visibleKeys.includes(m.key));
    // Comparisons replay the first visible series in the legend order.
    const comparisonOf = interval === "Day" ? visibleMetrics[0] ?? null : null;
    const activeComparisons: IChartComparison[] = comparisonOf
        ? COMPARISONS.filter((c) => comparisonIds.includes(c.id)).map((c) => ({ key: `cmp_${c.id}`, label: c.label, color: c.color }))
        : [];

    const buckets = useMemo(() => buildBuckets(startDate, endDate, interval), [startDate, endDate, interval]);

    const chartData = useMemo<IChartPoint[]>(() => {
        const rows = rowsRef.current;
        const byBucket = new Map<string, Record<MetricKey, number>>();
        rows.forEach((r) => {
            if (r.date < startDate || r.date > endDate) return;
            const key = bucketKey(r.date, interval);
            const acc = byBucket.get(key) ?? Object.fromEntries(METRIC_KEYS.map((key) => [key, 0]));
            METRIC_KEYS.forEach((k) => (acc[k] += Number(r[k]) || 0));
            byBucket.set(key, acc);
        });
        return buckets.map((b) => {
            const values = byBucket.get(b.key);
            const point: IChartPoint = { key: b.key, label: b.label };
            METRIC_KEYS.forEach((k) => (point[k] = values ? values[k] : null));
            if (comparisonOf) {
                COMPARISONS.filter((c) => comparisonIds.includes(c.id)).forEach((c) => {
                    const past = rows.get(dayjs(b.key).subtract(c.offsetDays, "day").format(YMD));
                    point[`cmp_${c.id}`] = past ? Number(past[comparisonOf.key as MetricKey]) || 0 : null;
                });
            }
            return point;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowsVersion, buckets, startDate, endDate, interval, comparisonIds, comparisonOf?.key, scopeKey, METRIC_KEYS]);

    const windowDaysOf = (key: MetricKey): number => {
        const metric = metricOf.get(key);
        if (metric?.countingMode === "attributed" && metric.dateBasis === "impression") return metric.attributionWindowDays;
        return metric?.countingMode === "raw" ? 0 : 1;
    };
    const maturingDays = Math.max(0, ...visibleMetrics.map((m) => windowDaysOf(m.key as MetricKey)));
    const maturingFromKey = maturingFrom(buckets, interval, maturingDays);
    const maturingIsOnlyToday = maturingDays <= 1;
    const bucketKeyOf = (d: dayjs.Dayjs) => d.format(interval === "Month" ? "YYYY-MM" : YMD);
    const maturingWindows: IMaturingWindow[] = visibleMetrics.flatMap((m) => {
        const days = windowDaysOf(m.key as MetricKey);
        if (days <= 1) return [];
        const from = dayjs().subtract(days, "day").startOf("day").add(1, "day");
        const fromKey = bucketKeyOf(from);
        const partialKey = interval === "Month" && from.date() !== 1 && buckets.some((b) => b.key === fromKey) ? fromKey : null;
        const fullKey = buckets.find((b) => (partialKey ? b.key > fromKey : b.key >= fromKey))?.key ?? null;
        return partialKey || fullKey ? [{ key: m.key, label: m.label, color: m.color, days, from: from.format(YMD), partialKey, fullKey }] : [];
    });
    // The paying event's window sets the hatch whenever it is on the chart.
    const primaryKey = metrics.find((m) => m.primary)?.key;
    const hatchWindows = maturingWindows.some((w) => w.key === primaryKey) ? maturingWindows.filter((w) => w.key === primaryKey) : maturingWindows;
    const hatchFromKey = maturingWindows.length
        ? hatchWindows.map((w) => w.fullKey).filter((k): k is string => !!k).sort()[0] ?? null
        : maturingFromKey;

    const isStillMaturing = (key: MetricKey): boolean => {
        const metric = metricOf.get(key);
        const days = metric?.countingMode === "attributed" && metric.dateBasis === "impression" ? metric.attributionWindowDays : 0;
        return !!days && dayjs(endDate).isAfter(dayjs().subtract(days, "day"));
    };

    const rangeLabel = `${dayjs(startDate).format("MMM D")} – ${dayjs(endDate).format("MMM D, YYYY")}`;
    const basisLabel = dateBasis === "conversion" ? "by conversion date" : "by impression date";
    const loading = rangeStatus === "loading" || !windowSettled;
    const isEmpty = totals && metrics.length > 0 && METRIC_KEYS.every((key) => totals[key] === 0);

    const summary = totals
        ? `${interval === "Day" ? "Daily" : "Monthly"} performance for ${campaign}, ${rangeLabel}, ${basisLabel}. Totals: ${campaignMetrics.map((m) => `${formatMetricValue(totals[m.key as MetricKey], m.decimals)} ${m.label.toLowerCase()}`).join(", ")}.`
        : `Performance for ${campaign}, ${rangeLabel}. Loading.`;

    const setRange = (start: string, end: string) => {
        setStartDate(start);
        setEndDate(end);
    };

    if (scopeStatus === "error") return <Alert severity="error">Couldn't load campaign access.</Alert>;

    if (scopeStatus === "ready" && campaigns.length === 0) {
        return (
            <Paper sx={{ border: 1, borderColor: "divider", boxShadow: "none", borderRadius: "12px", p: 3 }}>
                <Typography variant="h6">No campaigns assigned to your account</Typography>
                <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                    Your account isn't linked to any campaign yet. Contact your account manager to get access.
                </Typography>
            </Paper>
        );
    }

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1, flexWrap: "wrap", gap: 1.5 }}>
                <TimeRangeSelector start={startDate} end={endDate} onChange={setRange} />
                <ToggleButtonGroup exclusive size="small" aria-label="Interval" value={interval} onChange={(_e: unknown, v: Interval | null) => v && setIntervalValue(v)}>
                    <ToggleButton value="Day" sx={{ px: 2 }}>Daily</ToggleButton>
                    <ToggleButton value="Month" sx={{ px: 2 }}>Monthly</ToggleButton>
                </ToggleButtonGroup>
                <Box sx={{ flex: 1 }} />
                <ToggleButtonGroup exclusive size="small" aria-label="Reporting date basis" value={dateBasis} onChange={(_e: unknown, v: DateBasis | null) => v && setDateBasis(v)}>
                    <ToggleButton value="impression" sx={{ px: 2 }}>Impression date</ToggleButton>
                    <ToggleButton value="conversion" sx={{ px: 2 }}>Conversion date</ToggleButton>
                </ToggleButtonGroup>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                {dateBasis === "conversion"
                    ? "Attributed events by event date; impressions by served date."
                    : "Attributed events by the date of their impressions; impressions by served date."}{" "}
                {metrics.some((m) => m.countingMode === "raw") && "Raw advertiser events always use event date and are unattributed. "}
                Dates are UTC; attribution credit is unchanged.
            </Typography>

            {rangeStatus === "error" && (
                <Alert
                    severity="error"
                    role="alert"
                    sx={{ mb: 2.5 }}
                    action={
                        <Button color="inherit" size="small" onClick={() => setReloadKey((k) => k + 1)}>
                            Retry
                        </Button>
                    }
                >
                    Couldn't load dashboard data. Your filters are kept; try again.
                </Alert>
            )}

            {/* The preview clears when the pointer leaves the whole row, so crossing the gap between two cards cannot flicker. */}
            <Box onMouseLeave={() => setPreviewKey(null)} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: `repeat(${Math.max(shownKeys.length, 1)}, minmax(0, 1fr))` }, gap: 1.25, mb: 2.5 }}>
                {shownKeys.map((key) => {
                    const metric = chartMetrics.find((m) => m.key === key)!;
                    const maturing = isStillMaturing(key);
                    const isEvent = metric.type === "line";
                    return (
                        <MetricCard
                            key={key}
                            label={metricOf.get(key)!.label}
                            help={metricHelp(metricOf.get(key)!)}
                            dotColor={metric.color}
                            value={totals ? formatMetricValue(totals[key], metric.decimals) : "—"}
                            sub={`${rangeLabel} · ${metricBasisLabel(metricOf.get(key)!)}`}
                            loading={!totals && loading}
                            refreshing={!!totals && loading}
                            badge={maturing ? <Chip size="small" label="Still maturing" sx={maturingChipSx} /> : undefined}
                            onMouseEnter={
                                isEvent
                                    ? () => {
                                          setFocusKey(key);
                                          setPreviewKey(visibleKeys.includes(key) ? null : key);
                                      }
                                    : () => setPreviewKey(null)
                            }
                            onClick={
                                isEvent
                                    ? () => {
                                          toggleSeries(key);
                                          setPreviewKey(null);
                                      }
                                    : undefined
                            }
                        />
                    );
                })}
            </Box>

            <Paper sx={{ border: 1, borderColor: "divider", boxShadow: "none", borderRadius: "12px", p: 2.5, mb: 2.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
                    <Typography variant="subtitle2" component="h2" sx={{ color: "text.secondary", mr: 1 }}>
                        Performance
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="caption" color="text.secondary">
                        {comparisonOf ? `Compare ${comparisonOf.label.toLowerCase()} with` : interval === "Month" ? "Comparisons are available in the Daily view" : "Show a metric to compare"}
                    </Typography>
                    {COMPARISONS.map((c) => {
                        const on = comparisonIds.includes(c.id);
                        return (
                            <Chip
                                key={c.id}
                                label={c.label}
                                size="small"
                                clickable
                                disabled={!comparisonOf}
                                aria-pressed={on}
                                onClick={() => setComparisonIds((prev) => (on ? prev.filter((id) => id !== c.id) : [...prev, c.id]))}
                                variant={on ? "filled" : "outlined"}
                                sx={on ? { bgcolor: c.color, color: "common.white", "&:hover": { bgcolor: c.color } } : undefined}
                            />
                        );
                    })}
                </Box>

                {isEmpty && !loading ? (
                    <Box sx={{ height: CHART_HEIGHT, display: "grid", placeItems: "center", textAlign: "center" }}>
                        <Box>
                            <Typography variant="h6">No activity in this range</Typography>
                            <Typography color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                                The campaign may not have started yet, or it was paused for these dates.
                            </Typography>
                            <Button variant="outlined" onClick={() => setRange(presetRange(90).start, presetRange(90).end)}>
                                Show last 90 days
                            </Button>
                        </Box>
                    </Box>
                ) : loading && metrics.length === 0 ? (
                    <DashboardLoadingChart height={CHART_HEIGHT} spinner label={<>Loading performance… <RunTimer /></>} />
                ) : (
                    <PerformanceChart
                        data={chartData}
                        metrics={campaignMetrics}
                        visibleKeys={chartVisibleKeys}
                        enabledKeys={visibleKeys}
                        onToggle={toggleSeries}
                        onReset={() => setHiddenEvents(null)}
                        focusKey={focusKey}
                        onFocusChange={setFocusKey}
                        comparisons={activeComparisons}
                        comparisonOf={comparisonOf}
                        maturingFromKey={hatchFromKey}
                        maturingWindows={maturingWindows}
                        loading={loading}
                        height={CHART_HEIGHT}
                        compact={compact}
                        summary={summary}
                    />
                )}

                {(hatchFromKey || maturingWindows.length > 0) && !isEmpty && (windowSettled || chartData.some((p) => p.impressions !== null)) && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1, textAlign: "center" }}>
                        {maturingIsOnlyToday
                            ? "Today is still in progress."
                            : "Shaded dates are still maturing."}
                    </Typography>
                )}
            </Paper>
        </Box>
    );
};

const Dashboard = (): JSX.Element => {
    const { campaign } = useCampaignScope();
    const organizationId = useAppSelector((state: any) => state.auth?.selectedOrganizationId);
    // Remount scope-local state and localStorage preferences before rendering another client's data.
    return <DashboardContent key={JSON.stringify([organizationId, campaign])} />;
};

export default Dashboard;
